import supabase from "@/services/supabaseClient";

function asErrorMessage(input, fallback = "Request failed.") {
  if (!input) return fallback;
  if (typeof input === "string") return input;
  return input.message || fallback;
}

function parseNotes(notes) {
  if (!notes) return null;
  if (typeof notes === "object") return notes;
  try {
    return JSON.parse(String(notes));
  } catch {
    return null;
  }
}

function normaliseUploadRow(row) {
  if (!row) return null;
  const fields = parseNotes(row.notes);
  return {
    id: row.id,
    status: String(row.status || "pending"),
    created_at: row.created_at || null,
    notes: row.notes || null,
    ocrSummary: fields ? { fields } : null,
  };
}

export function resolveCanonicalKycState(status, { hasUpload = true } = {}) {
  const normalized = String(status || "").trim().toLowerCase();
  if (!hasUpload && !normalized) return "";
  if (normalized === "verified") return "verified";
  if (["admin_reviewed", "approved", "passed"].includes(normalized)) return "ocr_completed";
  if (["rejected", "failed", "error"].includes(normalized)) return "rejected";
  if (["ocr_done", "done", "partial"].includes(normalized)) return "ocr_completed";
  if (["pending", "processing", "in_progress", "uploading"].includes(normalized)) return "ocr_pending";
  return hasUpload ? "identity_submitted" : "";
}

export function resolveUploadId(meta) {
  return (
    meta?.id ||
    meta?.upload_id ||
    meta?.uploadId ||
    null
  );
}

function pad2(value) {
  return String(value).padStart(2, "0");
}

function isValidYmd(y, m, d) {
  const dt = new Date(y, m - 1, d);
  return (
    Number.isFinite(y) &&
    Number.isFinite(m) &&
    Number.isFinite(d) &&
    dt.getFullYear() === y &&
    dt.getMonth() === m - 1 &&
    dt.getDate() === d
  );
}

/**
 * Best-effort conversion of OCR date strings to YYYY-MM-DD.
 * Accepts ISO, DD/MM/YYYY, DD.MM.YYYY, DD-MM-YYYY.
 */
export function toIsoDateLoose(input) {
  const raw = String(input || "").trim();
  if (!raw) return "";

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const y = Number(iso[1]);
    const m = Number(iso[2]);
    const d = Number(iso[3]);
    return isValidYmd(y, m, d) ? `${y}-${pad2(m)}-${pad2(d)}` : "";
  }

  const dmy = raw.match(/^(\d{2})[\/.\-](\d{2})[\/.\-](\d{4})$/);
  if (dmy) {
    const d = Number(dmy[1]);
    const m = Number(dmy[2]);
    const y = Number(dmy[3]);
    return isValidYmd(y, m, d) ? `${y}-${pad2(m)}-${pad2(d)}` : "";
  }

  return "";
}

export async function uploadDocumentForOcr({
  file,
  docType = "passport",
  profileTable = "",
  dependantId = "",
  docNumber = "",
  country = "",
  retentionDays = 90,
}) {
  if (!(file instanceof File)) {
    throw new Error("File required.");
  }

  const form = new FormData();
  form.append("file", file);
  form.append("doc_type", docType);

  if (profileTable) form.append("profile_table", profileTable);
  if (dependantId) form.append("dependant_id", dependantId);
  if (docNumber) form.append("doc_number", docNumber);
  if (country) form.append("country", country);
  if (retentionDays) form.append("retention_days", String(retentionDays));

  const { data, error } = await supabase.functions.invoke("upload-id", {
    body: form,
  });

  if (error) {
    throw new Error(asErrorMessage(error, "Upload failed."));
  }

  return data || {};
}

export async function readUploadStatus(uploadId) {
  if (!uploadId) return null;

  const { data, error } = await supabase
    .from("id_uploads")
    .select("id, status, notes, created_at")
    .eq("id", uploadId)
    .maybeSingle();

  if (error) {
    throw new Error(asErrorMessage(error, "Could not read upload status."));
  }

  return normaliseUploadRow(data);
}

export function subscribeToUploadStatus(uploadId, { onUpdate, onError } = {}) {
  if (!uploadId) return () => {};

  let alive = true;

  const emit = (row) => {
    if (!alive || !row) return;
    onUpdate?.(normaliseUploadRow(row));
  };

  readUploadStatus(uploadId)
    .then((row) => row && emit(row))
    .catch((err) => {
      if (alive) onError?.(err);
    });

  const channel = supabase
    .channel(`verify-check-${uploadId}`)
    .on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "id_uploads",
        filter: `id=eq.${uploadId}`,
      },
      (payload) => emit(payload?.new)
    )
    .subscribe();

  return () => {
    alive = false;
    supabase.removeChannel(channel);
  };
}
